import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import {
  ChartBarIcon,
  CircleStackIcon,
  ChatBubbleLeftRightIcon,
  UsersIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  MoonIcon,
  SunIcon,
  SparklesIcon,
  BuildingOffice2Icon,
  ChevronUpDownIcon,
  LinkIcon,
  CalculatorIcon,
  CubeIcon
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Dashboards', href: '/dashboards', icon: ChartBarIcon },
  { name: 'Data Sources', href: '/data', icon: CircleStackIcon },
  { name: 'AI Assistant', href: '/ai', icon: ChatBubbleLeftRightIcon }
];

// Data Studio navigation (formerly Data Pipeline)
const dataStudioNavigation = [
  { name: 'Data Studio', href: '/data/studio', icon: SparklesIcon, primary: true },
  { name: 'Data Connections', href: '/data/relationships', icon: LinkIcon },
  { name: 'Business Metrics', href: '/data/metrics', icon: CalculatorIcon },
  { name: 'Analysis Views', href: '/data/marts', icon: CubeIcon }
];

// Super Admin only navigation
const superAdminNavigation = [
  { name: 'Companies', href: '/admin/companies', icon: BuildingOffice2Icon }
];

const adminNavigation = [
  { name: 'Users', href: '/admin/users', icon: UsersIcon, permission: 'manage_company_users' },
  { name: 'Settings', href: '/admin/settings', icon: Cog6ToothIcon, permission: 'manage_own_company' },
  { name: 'Billing', href: '/admin/billing', icon: CreditCardIcon, permission: 'view_billing' }
];

export default function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const { user, company, selectedCompany, logout, hasPermission, isSuperUser, getCurrentCompany } = useAuthStore();
  const navigate = useNavigate();
  
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };
  
  const filteredAdminNav = adminNavigation.filter(item => 
    !item.permission || hasPermission(item.permission)
  );
  
  // Get the current working company (selected or user's own)
  const currentCompany = getCurrentCompany();
  const isSuper = isSuperUser();
  
  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-950">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-white dark:bg-surface-900 
        border-r border-surface-200 dark:border-surface-800
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-surface-200 dark:border-surface-800">
            <NavLink to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                <SparklesIcon className="w-5 h-5 text-white" />
              </div>
              <span className="font-display font-bold text-lg text-surface-900 dark:text-white">
                Neoshift<span className="text-primary-600">BI</span>
              </span>
            </NavLink>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          {/* Company badge / switcher */}
          {isSuper ? (
            <NavLink 
              to="/admin/companies"
              className="block px-4 py-3 border-b border-surface-200 dark:border-surface-800 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-surface-500 dark:text-surface-400 mb-1">
                    {selectedCompany ? 'Working as' : 'No company selected'}
                  </div>
                  <div className="font-medium text-surface-900 dark:text-white truncate">
                    {currentCompany?.name || 'Select a company'}
                  </div>
                </div>
                <ChevronUpDownIcon className="w-5 h-5 text-surface-400 flex-shrink-0" />
              </div>
            </NavLink>
          ) : company && (
            <div className="px-4 py-3 border-b border-surface-200 dark:border-surface-800">
              <div className="text-xs text-surface-500 dark:text-surface-400 mb-1">Company</div>
              <div className="font-medium text-surface-900 dark:text-white truncate">
                {company.name}
              </div>
            </div>
          )}
          
          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {/* Main nav */}
            <div className="space-y-1">
              {navigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    isActive ? 'sidebar-link-active' : 'sidebar-link'
                  }
                >
                  <item.icon className="w-5 h-5" />
                  {item.name}
                </NavLink>
              ))}
            </div>
            
            {/* Data Studio nav */}
            <div className="pt-4 pb-2">
              <div className="text-xs font-medium text-surface-400 dark:text-surface-500 uppercase tracking-wider px-3">
                Data Studio
              </div>
            </div>
            <div className="space-y-1">
              {dataStudioNavigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    item.primary
                      ? isActive 
                        ? 'sidebar-link-active bg-gradient-to-r from-purple-500/10 to-pink-500/10' 
                        : 'sidebar-link hover:bg-gradient-to-r hover:from-purple-500/5 hover:to-pink-500/5'
                      : isActive ? 'sidebar-link-active' : 'sidebar-link'
                  }
                >
                  <item.icon className={`w-5 h-5 ${item.primary ? 'text-purple-500' : ''}`} />
                  {item.name}
                </NavLink>
              ))}
            </div>
            
            {/* Super Admin nav */}
            {isSuper && (
              <>
                <div className="pt-4 pb-2">
                  <div className="text-xs font-medium text-surface-400 dark:text-surface-500 uppercase tracking-wider px-3">
                    Platform
                  </div>
                </div>
                <div className="space-y-1">
                  {superAdminNavigation.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) =>
                        isActive ? 'sidebar-link-active' : 'sidebar-link'
                      }
                    >
                      <item.icon className="w-5 h-5" />
                      {item.name}
                    </NavLink>
                  ))}
                </div>
              </>
            )}
            
            {/* Admin nav */}
            {filteredAdminNav.length > 0 && (
              <>
                <div className="pt-4 pb-2">
                  <div className="text-xs font-medium text-surface-400 dark:text-surface-500 uppercase tracking-wider px-3">
                    Administration
                  </div>
                </div>
                <div className="space-y-1">
                  {filteredAdminNav.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) =>
                        isActive ? 'sidebar-link-active' : 'sidebar-link'
                      }
                    >
                      <item.icon className="w-5 h-5" />
                      {item.name}
                    </NavLink>
                  ))}
                </div>
              </>
            )}
          </nav>
          
          {/* User section */}
          <div className="p-4 border-t border-surface-200 dark:border-surface-800">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center text-white font-medium">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-surface-900 dark:text-white truncate">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="text-sm text-surface-500 dark:text-surface-400 truncate">
                  {user?.role?.replace('_', ' ')}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={toggleDarkMode}
                className="flex-1 btn-ghost text-sm"
              >
                {darkMode ? (
                  <SunIcon className="w-4 h-4 mr-2" />
                ) : (
                  <MoonIcon className="w-4 h-4 mr-2" />
                )}
                {darkMode ? 'Light' : 'Dark'}
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 btn-ghost text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </aside>
      
      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-white/80 dark:bg-surface-900/80 backdrop-blur-xl border-b border-surface-200 dark:border-surface-800">
          <div className="flex items-center justify-between h-full px-4 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800"
            >
              <Bars3Icon className="w-6 h-6 text-surface-600 dark:text-surface-400" />
            </button>
            
            <div className="flex-1" />
            
            {/* Quick actions could go here */}
          </div>
        </header>
        
        {/* Page content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

